import React from "react";
import { useSelector } from "react-redux";
import PropTypes, { arrayOf, number, shape } from "prop-types";
import MapShape from "components/MapShape";
import mapF0StaticData from "config/f0Info";
import mapF1StaticData from "config/f1Info";
import mapF2StaticData from "config/f2Info";
import mapF3StaticData from "config/f3Info";
import elevators from "config/elevators.json";
import playClickSpecial from "utils/playClickSpecial";
import MapTravelBar from "components/MapTravelBar/MapTravelBar";
import styles from "./styles.module.css";

import { getCachedItem } from "utils/localStorage";
import { deviceFloorLevel, deviceSvgId } from "config/constants";
import useGetFloorLvl from "hooks/useGetFloorLvl";
import switchMapInfoSet from "utils/switchMapInfo";
import { searchNearestFacility, searchNearestCommutation } from "./localLogic";

MapRouterGeneral.propTypes = {
  locationsHighlighted: PropTypes.string,
  floorIndex: PropTypes.number,
  bannerSrc: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  rotate: PropTypes.number,
  slotInfo: PropTypes.shape({
    floorLevel: number,
    svgId: number,
  }),
  pinsList: arrayOf(
    shape({
      x: number,
      y: number,
    })
  ),
};

function MapRouterGeneral(props) {
  const searchState = useSelector((state) => state.filter.filterByName);
  const facilities = useSelector((state) => state.facilities.facilities);
  const { rotate } = props;
  const floorLevel = useGetFloorLvl();
  //const mapRotation = rotate || getCachedItem(mapOrientation);
  const mapRotation = 0;

  const [mapInfoSetOfMainFloor, setMapInfoSetOfMainFloor] = React.useState({
    floorIndex: 0,
    vertices: [],
    connections: [],
  });

  const [pinsList, setPinsList] = React.useState([]);

  const switchMapInfoSetOfMainFloor = (floorNumber) => {
    // setState to be the static DB relevant floor ( including coordinates and connections )
    const staticDB = switchMapInfoSet(floorNumber);
    setMapInfoSetOfMainFloor(staticDB);
  };

  const deviceFloor = getCachedItem(deviceFloorLevel);
  const deviceLocation = getCachedItem(deviceSvgId);
  const [targetFacility, setTargetFacility] = React.useState();
  const [costToDestinationOfMainFloor, setCostToDestinationOfMainFloor] =
    React.useState(0);

  const [isTargetOnDifferntFloor, setIsTargetOnDifferntFloor] =
    React.useState(false);
  const [startPointOfDestinationFloor, setStartPointOfDestinationFloor] =
    React.useState(null);
  const [endPointOfMainFloor, setEndPointOfMainFloor] = React.useState(null);
  const [commuteByEscalator, setCommuteByEscalator] = React.useState(false);
  const [endPointOfDestinationFloor, setEndPointOfDestinationFloor] =
    React.useState(null);
  const [
    costToDestinationOfDestinationFloor,
    setCostToDestinationOfDestinationFloor,
  ] = React.useState(0);

  const [shouldShowMainFloor, setShouldShowMainFloor] = React.useState(true);
  const [destinationName, setDestinationName] = React.useState("");
  const pathToElevator = () => {
    if (isTargetOnDifferntFloor) {
      playClickSpecial();
      setShouldShowMainFloor(true);
    }
  };

  const pathToDestination = () => {
    if (isTargetOnDifferntFloor) {
      playClickSpecial();
      setShouldShowMainFloor(false);
    }
  };

  React.useEffect(() => {
    /* Logic is in progress */
  }, ["didMount"]);

  React.useEffect(() => {
    switchMapInfoSetOfMainFloor(props.floorIndex);
  }, [props.floorIndex]);

  React.useEffect(() => {
    let facilityType = null;
    for (let i = 0; i < facilities.length; i++) {
      let tags = `${facilities[i]?.tags}`.split(",").map((ele) => ele.trim());
      if (tags.includes(searchState)) {
        facilityType = facilities[i].type;
        setDestinationName(facilityType);
        break;
      }
    }
    if (!facilityType) {
      facilityType = props.locationsHighlighted;
    }

    if (facilityType) {
      switch (floorLevel) {
        case 0:
          if (deviceFloor === floorLevel) {
            const targetFacilityId0 = searchNearestFacility(
              deviceLocation,
              deviceFloor,
              mapF0StaticData[facilityType]
            )?.id;
            setTargetFacility(targetFacilityId0);
          }

          const newPinsList0 = mapF0StaticData[facilityType].map((svgId) => {
            const ele = mapF0StaticData.vertices.find(({ id }) => id === svgId);
            return {
              id: ele.id,
              x: ele.cx,
              y: ele.cy,
            };
          });
          setPinsList(newPinsList0);
          break;
        case 1:
          if (deviceFloor === floorLevel) {
            const targetFacilityId1 = searchNearestFacility(
              deviceLocation,
              deviceFloor,
              mapF1StaticData[facilityType]
            )?.id;
            setTargetFacility(targetFacilityId1);
          }

          const newPinsList1 = mapF1StaticData[facilityType].map((svgId) => {
            const ele = mapF1StaticData.vertices.find(({ id }) => id === svgId);
            return {
              id: ele.id,
              x: ele.cx,
              y: ele.cy,
            };
          });
          setPinsList(newPinsList1);
          break;
        case 2:
          let targetFacilityId2 = undefined;
          if (deviceFloor === floorLevel) {
            setIsTargetOnDifferntFloor(false);
            targetFacilityId2 = searchNearestFacility(
              deviceLocation,
              deviceFloor,
              mapF2StaticData[facilityType]
            )?.id;
            setTargetFacility(targetFacilityId2);
          }
          if (!targetFacilityId2) {
            setIsTargetOnDifferntFloor(true);
            const targetElevatorId = searchNearestCommutation(
              deviceLocation,
              deviceFloor,
              1
            )?.id;
            const targetElevatorItem = elevators.find(
              ({ id }) => id === targetElevatorId
            );
            // [Get-out point] Still the same elevator/escalator but on destination floor
            setStartPointOfDestinationFloor(targetElevatorItem.svgIdOnFloor[1]);
            // [Enter point] Targeted elevator/escalator on the device's floor
            setEndPointOfMainFloor(
              targetElevatorItem.svgIdOnFloor[deviceFloor]
            );
            // Change icon to elevator if the transportation is not escalator
            setCommuteByEscalator(targetElevatorItem.isEscalator);
            setEndPointOfDestinationFloor(
              searchNearestFacility(
                targetElevatorItem.svgIdOnFloor[1],
                1,
                mapF1StaticData[facilityType]
              )?.id
            );
          }
          const newPinsList2 = mapF2StaticData[facilityType].map((svgId) => {
            const ele = mapF2StaticData.vertices.find(({ id }) => id === svgId);
            return {
              id: ele.id,
              x: ele.cx,
              y: ele.cy,
            };
          });
          setPinsList(newPinsList2);
          break;
        case 3:
          let targetFacilityId3 = undefined;
          if (deviceFloor === floorLevel) {
            setIsTargetOnDifferntFloor(false);
            targetFacilityId3 = searchNearestFacility(
              deviceLocation,
              deviceFloor,
              mapF3StaticData[facilityType]
            )?.id;
            setTargetFacility(targetFacilityId3);
          }
          if (!targetFacilityId3) {
            setIsTargetOnDifferntFloor(true);
            const targetElevatorId = searchNearestCommutation(
              deviceLocation,
              deviceFloor,
              1
            )?.id;
            const targetElevatorItem = elevators.find(
              ({ id }) => id === targetElevatorId
            );
            // [Get-out point] Still the same elevator/escalator but on destination floor
            setStartPointOfDestinationFloor(targetElevatorItem.svgIdOnFloor[1]);
            // [Enter point] Targeted elevator/escalator on the device's floor
            setEndPointOfMainFloor(
              targetElevatorItem.svgIdOnFloor[deviceFloor]
            );
            // Change icon to elevator if the transportation is not escalator
            setCommuteByEscalator(targetElevatorItem.isEscalator);
            setEndPointOfDestinationFloor(
              searchNearestFacility(
                targetElevatorItem.svgIdOnFloor[1],
                1,
                mapF1StaticData[facilityType]
              )?.id
            );
          }

          const newPinsList3 = mapF3StaticData[facilityType].map((svgId) => {
            const ele = mapF3StaticData.vertices.find(({ id }) => id === svgId);
            return {
              id: ele.id,
              x: ele.cx,
              y: ele.cy,
            };
          });
          setPinsList(newPinsList3);
          break;
      }
    }
  }, [props.locationsHighlighted, floorLevel]);


  if (deviceFloor === floorLevel) {
    if (isTargetOnDifferntFloor) {
      return (
        <>
          <div
            className={
              !shouldShowMainFloor
                ? styles.local_display_none
                : styles.local_display_visible
            }
          >
            <MapShape
              {...props}
              rotate={mapRotation}
              // If there are two floors to traverse then this "end" is not a destination but an elevator/escalator
              start={deviceLocation}
              end={endPointOfMainFloor}
              vertices={mapInfoSetOfMainFloor?.vertices}
              floorIndex={mapInfoSetOfMainFloor?.floorIndex}
              getTravelCost={setCostToDestinationOfMainFloor}
              connections={mapInfoSetOfMainFloor?.connections}
              key={mapInfoSetOfMainFloor?.floorIndex + "_floor"}
              notDestinationButElevator={isTargetOnDifferntFloor}
            />
          </div>
          <div
            className={
              !isTargetOnDifferntFloor || shouldShowMainFloor
                ? styles.local_display_none
                : styles.local_display_visible
            }
          >
            <MapShape
              {...props}
              rotate={mapRotation}
              start={startPointOfDestinationFloor}
              end={endPointOfDestinationFloor}
              vertices={mapF1StaticData?.vertices}
              floorIndex={mapF1StaticData?.floorIndex}
              getTravelCost={setCostToDestinationOfDestinationFloor}
              connections={mapF1StaticData?.connections}
              key={mapF1StaticData?.floorIndex + "_floor"}
            />
          </div>

          <MapTravelBar
            isOnDestination={!shouldShowMainFloor}
            vertical={false}
            layout={"restaurant"}
            commuteByEscalator={commuteByEscalator}
            pathToElevator={pathToElevator}
            pathToDestination={pathToDestination}
            destinationName={props.locationsHighlighted || destinationName}
            isTargetOnDifferntFloor={isTargetOnDifferntFloor}
            currentFloorLevel={floorLevel}
            destinationFloorLevel={1}
            costToDestinationOfMainFloor={costToDestinationOfMainFloor}
            costToDestinationOfDestinationFloor={
              costToDestinationOfDestinationFloor
            }
          />
        </>
      );
    } else {
      return mapInfoSetOfMainFloor ? (
        <MapShape
          {...props}
          start={deviceLocation}
          end={targetFacility}
          rotate={mapRotation}
          key={(mapInfoSetOfMainFloor?.floorIndex || 0) + "_floor"}
          floorIndex={mapInfoSetOfMainFloor?.floorIndex || 0}
          getTravelCost={setCostToDestinationOfMainFloor}
          connections={mapInfoSetOfMainFloor?.connections}
          vertices={mapInfoSetOfMainFloor?.vertices}
        />
      ) : (
        false
      );
    }
  } else {
    return (
      <MapShape
        {...props}
        rotate={mapRotation}
        key={(mapInfoSetOfMainFloor?.floorIndex || 0) + "_floor"}
        floorIndex={mapInfoSetOfMainFloor?.floorIndex || 0}
        pinsList={pinsList}
        connections={mapInfoSetOfMainFloor?.connections}
        vertices={mapInfoSetOfMainFloor?.vertices}
      />
    );
  }
}

MapRouterGeneral.defaultProps = {
  floorIndex: getCachedItem(deviceFloorLevel),
};

export default MapRouterGeneral;
